/**
 * Document Form Shared Module
 * Shared utilities used by both order-form and accounting-document-form
 */

import { CurrencyFormatter } from '../utils/currency-formatter';

export interface CurrencyInfo {
  Code: string;
  Culture: string;
  FreeDeliveryLimit: number;
  ProductVatPercent?: number;
  DeliveryVatPercent?: number;
}

export interface CurrencyDataSource {
  [key: string]: CurrencyInfo;
}

/**
 * Calculates and updates the price totals for a single item row.
 * @param row - The item row element
 * @param formatPrice - A function that formats a number as a currency string
 */
export function calculateItemTotals(
  row: HTMLElement,
  formatPrice: (amount: number) => string
): void {
  const quantityInput = row.querySelector('.item-quantity') as HTMLInputElement;
  const priceInput = row.querySelector('.item-price') as HTMLInputElement;
  const vatInput = row.querySelector('.item-vat') as HTMLInputElement;
  const saleInput = row.querySelector('.item-sale') as HTMLInputElement;
  const totalWithoutVatElement = row.querySelector('.item-total-without-vat') as HTMLElement;
  const totalWithVatElement = row.querySelector('.item-total-with-vat') as HTMLElement;

  const quantity = parseFloat(quantityInput?.value || '0');
  const pricePerUnit = parseFloat(priceInput?.value || '0');
  const vatPercent = parseFloat(vatInput?.value || '0');
  const salePercent = parseFloat(saleInput?.value || '0');

  const discountedPricePerUnit = pricePerUnit * (1 - salePercent / 100);
  const totalWithoutVat = quantity * discountedPricePerUnit;
  const totalWithVat = totalWithoutVat * (1 + vatPercent / 100);

  if (totalWithoutVatElement) {
    totalWithoutVatElement.textContent = formatPrice(totalWithoutVat);
  }

  if (totalWithVatElement) {
    totalWithVatElement.textContent = formatPrice(totalWithVat);
  }
}

/**
 * Creates a formatPrice function bound to the current currency selection.
 * @param getCurrentCurrencyId - Function that returns the currently selected currency ID
 * @param currencies - The currency data source
 */
export function createFormatPrice(
  getCurrentCurrencyId: () => string,
  currencies: CurrencyDataSource
): (amount: number) => string {
  return (amount: number): string => {
    const currencyId = getCurrentCurrencyId();
    const currency = currencies[currencyId] || { Code: 'CZK', Culture: 'cs-CZ' };
    return CurrencyFormatter.formatPrice(amount, currency.Code, currency.Culture);
  };
}

/**
 * Initialize sidebar navigation
 * Handles smooth scrolling and active state tracking via IntersectionObserver.
 * Works with `.order-nav-link` anchor elements and `.order-section` section elements.
 */
export function initSidebarNavigation(): void {
  const navLinks = document.querySelectorAll<HTMLAnchorElement>('.order-nav-link');
  const sections = document.querySelectorAll<HTMLElement>('.order-section');

  if (navLinks.length === 0 || sections.length === 0) {
    return;
  }

  // Track if we're currently scrolling programmatically
  let isScrollingProgrammatically = false;
  let scrollTimeout: number | null = null;

  // Smooth scroll to section on click
  navLinks.forEach((link) => {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      const targetId = this.getAttribute('href');

      if (!targetId) return;

      const targetSection = document.querySelector<HTMLElement>(targetId);

      if (targetSection) {
        // Set flag to disable observer updates
        isScrollingProgrammatically = true;

        // Clear any existing timeout
        if (scrollTimeout) {
          window.clearTimeout(scrollTimeout);
        }

        // Update active state immediately
        navLinks.forEach((l) => l.classList.remove('active'));
        this.classList.add('active');

        // Scroll to section
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Re-enable observer after scroll completes (smooth scroll takes ~500-800ms)
        scrollTimeout = window.setTimeout(() => {
          isScrollingProgrammatically = false;
        }, 1000);
      }
    });
  });

  // Update active link on scroll using IntersectionObserver
  const observerOptions: IntersectionObserverInit = {
    threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    rootMargin: '-100px 0px -50% 0px',
  };

  // Track which section has the highest intersection ratio
  const intersectionRatios = new Map<Element, number>();

  const observer = new IntersectionObserver((entries) => {
    // Skip observer updates during programmatic scrolling
    if (isScrollingProgrammatically) {
      return;
    }

    // Update intersection ratios for all entries
    entries.forEach((entry) => {
      intersectionRatios.set(entry.target, entry.intersectionRatio);
    });

    // Check if user scrolled to bottom of page
    const scrolledToBottom =
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10;

    let mostVisibleSection: Element | null = null;

    if (scrolledToBottom) {
      // If at bottom, activate the last section
      mostVisibleSection = sections[sections.length - 1];
    } else {
      // Find the section with the highest intersection ratio
      let maxRatio = 0;

      intersectionRatios.forEach((ratio, section) => {
        if (ratio > maxRatio) {
          maxRatio = ratio;
          mostVisibleSection = section;
        }
      });
    }

    // Update active link to the most visible section
    if (mostVisibleSection) {
      const sectionId = mostVisibleSection.id;
      const correspondingLink = document.querySelector<HTMLAnchorElement>(`[href="#${sectionId}"]`);

      navLinks.forEach((link) => link.classList.remove('active'));
      if (correspondingLink) {
        correspondingLink.classList.add('active');
      }
    }
  }, observerOptions);

  sections.forEach((section) => observer.observe(section));

  // The observer's -50% bottom margin means the last section may never enter the
  // observed root on short forms (e.g. accounting-document with only 4 sections).
  // A dedicated scroll listener handles this edge-case independently.
  let scrollRafId: number | null = null;

  const handleScrollBottom = (): void => {
    if (scrollRafId !== null) return;
    scrollRafId = window.requestAnimationFrame(() => {
      scrollRafId = null;
      if (isScrollingProgrammatically) return;

      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10;

      if (scrolledToBottom) {
        const lastSection = sections[sections.length - 1];
        const sectionId = lastSection.id;
        const correspondingLink = document.querySelector<HTMLAnchorElement>(
          `[href="#${sectionId}"]`
        );

        navLinks.forEach((link) => link.classList.remove('active'));
        if (correspondingLink) {
          correspondingLink.classList.add('active');
        }
      }
    });
  };

  window.addEventListener('scroll', handleScrollBottom, { passive: true });
}
